/**
 * 清新薄荷模版 - 首页（v4）
 * 
 * 设计核心理念：分类信息网站 + 现代卡片式UI
 * 与红色模版（左侧栏+数据表格）完全不同的布局：
 * - 顶部：大号居中搜索 Hero 区域（视觉重心）
 * - 中段：分类图标网格（4-5列卡片式）
 * - 底部：信息卡片瀑布流（非表格）
 * - 数据密度较低，视觉更舒适
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/templates/teal/components/Navbar";
import Footer from "@/templates/teal/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function TealHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 12)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 20 })
      .then((data) => {
        setListings(data.list || []);
        setTotalListings(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const iconColors = [
    "bg-teal-50 text-teal-600", "bg-emerald-50 text-emerald-600",
    "bg-cyan-50 text-cyan-600", "bg-blue-50 text-blue-500",
    "bg-green-50 text-green-600", "bg-sky-50 text-sky-600",
    "bg-indigo-50 text-indigo-500", "bg-purple-50 text-purple-500",
  ];

  return (
    <div className="min-h-screen bg-[#f8fafb]">
      <Navbar />

      {/* ── 搜索 Hero 区：大号居中搜索（与红色模版的关键差异点） ── */}
      <section className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-700 pt-24 pb-20">
        {/* 装饰 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/5 rounded-full" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-emerald-300/10 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-4 h-4 bg-white/20 rounded-full" />
          <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-white/20 rounded-full" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            发现身边的<span className="text-amber-300">好服务</span>
          </h1>
          <p className="text-teal-100/90 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            覆盖全城的分类信息平台，轻松发布、快速找到需要的服务
          </p>

          {/* 核心搜索框 */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto">
            <div className="flex items-center bg-white rounded-2xl shadow-2xl shadow-black/10 overflow-hidden p-1.5">
              <div className="flex-1 flex items-center pl-4">
                <i className="ri-search-line text-gray-400 text-lg mr-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="输入关键词搜索..."
                  className="flex-1 py-3 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all shadow-md cursor-pointer shrink-0"
              >
                <i className="ri-search-line mr-1.5" />搜索
              </button>
            </div>
            {showSearchTip && (
              <p className="mt-2 text-xs text-teal-100 flex items-center justify-center gap-1">
                <i className="ri-error-warning-line" />
                {searchConfig.disabledMessage || "搜索功能已关闭"}
              </p>
            )}
          </form>

          {/* 搜索热词 */}
          <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
            <span className="text-xs text-teal-100/70">热门搜索：</span>
            {categories.slice(0, 5).map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="text-xs text-white/80 hover:text-white px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── 分类图标网格（与红色左侧栏列表完全不同的展示方式）── */}
      <section className="relative -mt-10 z-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-grid-line text-teal-500" />
                全部分类
              </h2>
              <Link to="/category" className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {loadingCategories ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="animate-pulse flex flex-col items-center gap-2 p-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-2xl" />
                    <div className="w-14 h-3 bg-gray-100 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {categories.map((cat, idx) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                  >
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-xl transition-transform group-hover:scale-110 duration-200 ${iconColors[idx % iconColors.length]}`}>
                      <i className={cat.icon || 'ri-folder-line'} />
                    </div>
                    <span className="text-xs text-gray-600 group-hover:text-teal-600 font-medium text-center leading-tight">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── 中部统计栏 ── */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm">
              <div className="text-2xl font-bold text-teal-600">{totalListings.toLocaleString()}</div>
              <div className="text-xs text-gray-400 mt-1">服务信息</div>
            </div>
            <div className="bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm">
              <div className="text-2xl font-bold text-emerald-600">{categories.length}</div>
              <div className="text-xs text-gray-400 mt-1">分类数量</div>
            </div>
            <div className="bg-white rounded-xl p-5 text-center border border-gray-100 shadow-sm">
              <div className="text-2xl font-bold text-cyan-600">{11}</div>
              <div className="text-xs text-gray-400 mt-1">覆盖城市</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 信息卡片网格（核心差异：卡片流替代数据表格）── */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <i className="ri-file-list-3-line text-teal-500" />
              最新信息
            </h2>
            <Link to="/category" className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
              查看更多 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="w-3/4 h-5 bg-gray-200 rounded" />
                    <div className="w-1/2 h-4 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length > 0 ? (
            <>
              {/* 置顶信息横向滚动 */}
              {listings.filter(l => l.is_pinned).length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-pushpin-line" />置顶推荐
                    </span>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                    {listings.filter(l => l.is_pinned).map((item) => (
                      <Link
                        key={item.id}
                        to={`/listing/${item.id}`}
                        className="shrink-0 w-64 bg-gradient-to-br from-white to-teal-50/30 rounded-xl border border-teal-100 overflow-hidden hover:shadow-md hover:border-teal-300 transition-all group"
                      >
                        <div className="aspect-[16/9] bg-gray-100 overflow-hidden flex items-center justify-center">
                          {item.images?.[0] ? (
                            <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <i className="ri-image-line text-gray-300 text-3xl" />
                          )}
                        </div>
                        <div className="p-3.5">
                          <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 group-hover:text-teal-600 transition-colors">{item.title}</h3>
                          <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-2">
                            <span>{item.category?.name || "未分类"}</span>
                            <span>·</span>
                            <span>{formatDate(item.created_at)}</span>
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* 普通信息卡片网格 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {listings.map((item) => (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all duration-300 group flex flex-col"
                  >
                    <div className="aspect-[4/3] bg-gray-50 overflow-hidden flex items-center justify-center relative">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <i className="ri-image-line text-gray-300 text-3xl" />
                          <span className="text-[10px] text-gray-300">暂无图片</span>
                        </div>
                      )}
                      {item.is_pinned && (
                        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold shadow-sm">
                          顶
                        </span>
                      )}
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-[15px] font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-teal-600 transition-colors mb-auto">
                        {item.title}
                      </h3>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                        <span className="text-xs px-2 py-0.5 bg-teal-50 text-teal-600 rounded-full font-medium">
                          {item.category?.name || "未分类"}
                        </span>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          <i className="ri-time-line text-[10px]" />
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalListings > listings.length && (
                <div className="text-center mt-8">
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-teal-200 text-teal-600 font-semibold rounded-xl hover:bg-teal-50 hover:border-teal-300 transition-all"
                  >
                    查看全部 <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-teal-50">
                <i className="ri-inbox-line text-2xl text-teal-400" />
              </div>
              <p className="text-gray-500 font-medium">暂无信息</p>
              <p className="text-gray-400 text-sm mt-1">快来发布第一条信息吧</p>
              <Link to="/publish" className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors">
                <i className="ri-add-line" />立即发布
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
